import * as React from 'react';
import { cn } from '@/utils/cn';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { DialogClose } from '@radix-ui/react-dialog';
import { cva } from 'class-variance-authority';

import { Icons } from '../icons';

const Dialog = ({ children, ...props }: DialogPrimitive.DialogProps) => (
  <DialogPrimitive.Root {...props}>{children}</DialogPrimitive.Root>
);
const DialogTrigger = DialogPrimitive.Trigger;

interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  controlled?: boolean;
  theme?: 'light' | 'dark';
}

const asideDialogClassnames = cva('', {
  variants: {
    theme: {
      light: 'bg-white text-ink',
      dark: 'bg-ink text-white',
    },
  },
});

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    { className, children, controlled = false, theme = 'light', ...props },
    ref
  ) => (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 bg-ink/30 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in" />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed right-0 top-0 h-full w-full max-w-[calc(100%-40px)] bg-white shadow-soft lg:max-w-xl',
          'data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=open]:slide-in-from-right',
          'data-[state=closed]:duration-150 data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right',
          'overflow-auto p-6 py-8',
          asideDialogClassnames({ theme }),
          theme === 'dark' && 'dark',
          className
        )}
        {...props}
      >
        {children}

        {!controlled ? (
          <DialogPrimitive.Close
            className={cn(
              'absolute right-6 top-8 z-50 flex size-9 items-center justify-center rounded-full transition-opacity',
              'bg-fog text-ink'
            )}
          >
            <Icons.x className="size-5" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'mb-4 flex shrink-0 flex-col justify-center space-y-1.5 text-center sm:text-left',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('bits-text-h2 text-left', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn(className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog as AsideDialog,
  DialogContent as AsideDialogContent,
  DialogHeader as AsideDialogHeader,
  DialogTitle as AsideDialogTitle,
  DialogDescription as AsideDialogDescription,
  DialogTrigger as AsideDialogTrigger,
  DialogClose as AsideDialogClose,
};
